import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Heading } from 'components/Heading';
import { emitMessage, getAvatarUrl, socket, useIsAdmin } from 'engine/api';
import type { MessageReturnTypes } from 'shared/modelTypes';
import { useState, useEffect, useRef, type FC } from 'react';
import {
	type ChatMessageRecord,
	type TaskAnswer,
	type RoundState,
	type TaskStateRecord,
	type TeamRecord,
	type FileUpload,
	TeamDataSchema,
	type TaskState,
	type TaskStatus,
} from 'shared/modelTypes/client';
import { ChatOverview } from 'components/ChatOverview';
import { AdminCard } from 'components/AdminCard';
import { Para } from 'components/Para';
import { Button } from 'components/Button';
import { NumberField, TextField } from 'components/FormComponents';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { GameRecord, GameRound, GameTask } from 'shared/modelTypes/game';
import { Badge } from 'components/Badge';
import { EventMap } from 'components/maps/EventMap';
import {
	Camera,
	Check,
	CheckSquare2,
	ChevronRight,
	ChevronUp,
	DoorOpen,
	DownloadIcon,
	GamepadIcon,
	Info,
	ListCheck,
	Loader2,
	ListChecks,
	LockIcon,
	LockOpenIcon,
	Map,
	Maximize2,
	MessagesSquare,
	Minimize2,
	NotebookPen,
	OctagonX,
	Pencil,
	PencilLine,
	Play,
	Rotate3D,
	SquareX,
	Ticket,
	Trash2,
	Trophy,
	UserLock,
	Video,
	Wrench,
	type LucideIcon,
	Crown,
	Glasses,
	Snowflake,
	Sun,
	FerrisWheel,
	Archive,
	ArchiveRestore,
} from 'lucide-react';
import { FilterControls } from 'components/FiltersControls';
import { ModalButton } from 'components/ModalButton';
import type { EventState, EventStatus } from 'shared/modelTypes/event';
import { toast } from 'sonner';
import { InlineCode } from 'components/InlineCode';
import { getStorageUrl, toSentenceCase, unreachable } from 'shared/util';
import { TinySkinPreview } from 'components/TinySkinPreview';
import type { TaskMarker, TaskOrRoundMarker } from 'components/maps/EventMap';
import { Tooltip } from 'components/Tooltip';
import { BunnyVideoPlayer } from 'components/BunnyVideoPlayer';
import { Spacer } from 'components/Spacer';
import { Icon } from 'components/Icon';
import { GameRoundAvailabilityOptions } from 'forms/GameFormConstants';
import { TeamAvatar } from 'components/TeamAvatar';
import { Link } from '@tanstack/react-router';
import { useModal } from 'engine/modalContext';
import { Modal } from 'components/Modal';
import { Toggle } from 'components/Toggle';
import { RichTextContent } from 'components/RichTextContent';
import { GameOverview } from 'components/GameOverview';
import type { MediaNode } from 'shared/modelTypes/media';
import { FilePickerProvider } from 'components/FilePickerProvider';
import { closest, distance } from 'fastest-levenshtein';
import { bingoSetToPath } from 'shared/modelTypes/bingo';
import { appConfig } from 'engine/appConfig';

export const Route = createFileRoute('/dashboard/events/view/$id')({
	component: RouteComponent,
	loader: async () => {
		return {
			directory: (await emitMessage({ kind: 'media-list-directory', folderId: null })).media,
		};
	},
});

function RouteComponent() {
	const { id } = Route.useParams();

	const data = useEventOverview(id);

	if (data && 'event' in data) {
		return <EventOverview eventOverview={data} />;
	} else {
		return <span>loading...</span>;
	}
}

export const TabButton: FC<{ icon: string | LucideIcon; active: boolean; label: string; count?: number; onClick: () => void }> = ({
	icon,
	active,
	label,
	count = 0,
	onClick,
}) => {
	return (
		<button data-active={active ? true : undefined} onClick={onClick} className={'group relative flex-1 shrink py-3 text-sm font-bold'}>
			<span className="inline-flex gap-2 px-4">
				<Icon
					icon={icon}
					className="pointer-events-none size-5 shrink-0 p-[2px] text-gray-500 group-data-[active]:text-theme-400 dark:text-gray-400 dark:group-data-[active]:text-theme-900"
				/>

				<span className="hidden md:block">{label}</span>
				{count > 0 && <span className="opacity-70">{count}</span>}
			</span>
			<div className="absolute bottom-0 h-1 w-full bg-transparent group-data-[active]:bg-theme-400" />
		</button>
	);
};

const RuntimeBadge = ({ runningSince }: { runningSince: Date | null | undefined }) => {
	const [runtime, setRuntime] = useState(runningSince ? new Date().getTime() - runningSince.getTime() : 0);

	useEffect(() => {
		const interval = setInterval(() => {
			if (runningSince) {
				setRuntime(new Date().getTime() - runningSince.getTime());
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [runningSince]);

	const hours = Math.floor(runtime / (1000 * 60 * 60));
	const minutes = Math.floor((runtime % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((runtime % (1000 * 60)) / 1000);

	if (!runningSince) return null;

	return (
		<Badge status="info">
			<pre>
				Running for {hours.toString()}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
			</pre>
		</Badge>
	);
};

const TimeUnlockBadge = ({ unlockTime }: { unlockTime: Date | null | undefined }) => {
	const [timeLeft, setTimeLeft] = useState(unlockTime ? unlockTime.getTime() - new Date().getTime() : 0);

	useEffect(() => {
		const interval = setInterval(() => {
			if (unlockTime) {
				setTimeLeft(unlockTime.getTime() - new Date().getTime());
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [unlockTime]);

	const hours = Math.floor(timeLeft / (1000 * 60 * 60));
	const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

	if (!unlockTime) return null;
	if (timeLeft < 0) return null;

	return (
		<Badge status="info">
			<pre>
				Unlock in {hours ? hours.toString() + ':' : ''}
				{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
			</pre>
		</Badge>
	);
};

const EventOverview = ({ eventOverview }: { eventOverview: EventOverview }) => {
	const [activeTab, setActiveTab] = useState('info');
	const prevCountRef = useRef(0);
	const prevCountRefSubmissions = useRef(0);

	const { directory } = Route.useLoaderData();

	const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
	const submissionSoundRef = useRef<HTMLAudioElement | null>(null);
	useEffect(() => {
		notificationSoundRef.current = new Audio('/sfx/notification-ping-3.mp3');
		notificationSoundRef.current.load();

		submissionSoundRef.current = new Audio('/sfx/notification-ping-4.mp3');
		submissionSoundRef.current.load();
	}, []);

	const unreadMessageCount =
		eventOverview.chatMessages && eventOverview?.event?.state
			? getUnreadMessageCount(eventOverview.chatMessages, eventOverview.event?.state)
			: 0;

	// Sound for notifications
	useEffect(() => {
		if (unreadMessageCount > prevCountRef.current && notificationSoundRef.current) {
			notificationSoundRef.current.currentTime = 0;
			notificationSoundRef.current.play().catch((err) => console.warn('Could not play notification sound:', err));

			const lastMessage = eventOverview.chatMessages?.[eventOverview.chatMessages.length - 1];
			if (lastMessage && activeTab !== 'chat')
				toast(
					<div className="flex items-center gap-2" onClick={() => setActiveTab('chat')}>
						<TeamAvatar teamId={lastMessage?.teamId ?? ''} eventId={eventOverview?.event?.id ?? ''} />
						<span>
							<b>New message: </b>
							{lastMessage.attachments[0] && <i>{`Sent a ${lastMessage.attachments[0].kind}.`}</i>}
							{lastMessage.message}
						</span>
					</div>,
				);
		}
		prevCountRef.current = unreadMessageCount;
	}, [activeTab, eventOverview.chatMessages, eventOverview?.event?.id, unreadMessageCount]);

	const inProgressCount =
		eventOverview.taskSubmissions?.reduce((c, x) => {
			if (x.status === 'in-progress') c++;
			return c;
		}, 0) ?? 0;

	// Sound for new submissions
	useEffect(() => {
		if (inProgressCount > prevCountRefSubmissions.current && submissionSoundRef.current) {
			submissionSoundRef.current.currentTime = 0;
			submissionSoundRef.current.play().catch((err) => console.warn('Could not play notification sound:', err));

			const lastSubmission = eventOverview.taskSubmissions?.[eventOverview.taskSubmissions.length - 1];
			if (eventOverview.game && lastSubmission && activeTab !== 'submissions') {
				const task = eventOverview.game.game.rounds.flatMap((round) => round.tasks).find((t) => t.id === lastSubmission.taskId);
				toast(
					<div className="flex items-center gap-2" onClick={() => setActiveTab('submissions')}>
						<TeamAvatar teamId={lastSubmission?.teamId ?? ''} eventId={eventOverview?.event?.id ?? ''} />
						<span>
							<b>New submission: </b>
							{task?.title}
						</span>
					</div>,
				);
			}
		}
		prevCountRefSubmissions.current = inProgressCount;
	}, [inProgressCount]);

	const status = eventOverview?.event?.status;
	const statusLookup = { ended: 'warn', open: 'info', started: 'success', draft: 'info' } as const;

	return (
		<div className="mb-20 flex flex-col gap-6">
			<div className="flex items-baseline gap-2">
				<Heading mode="secondary">{eventOverview.event!.name}</Heading>
				<small className="hidden text-gray-500 dark:text-gray-400 md:block">{eventOverview?.variant?.name}</small>
				<div className="flex-1 shrink"></div>
				<div className="flex gap-2">
					{status === 'started' && (
						<RuntimeBadge
							runningSince={eventOverview.event!.state.startedAt ? new Date(eventOverview.event!.state.startedAt) : null}
						/>
					)}
					{status !== undefined && <Badge status={statusLookup[status]}>{toSentenceCase(status)}</Badge>}
				</div>
			</div>

			<div className="fixed bottom-0 left-0 z-10 flex w-full overflow-x-auto border border-gray-200 bg-white p-4 shadow-xl md:relative md:border-none md:bg-transparent md:p-0 md:shadow-none">
				<TabButton icon={Ticket} onClick={() => setActiveTab('info')} active={activeTab === 'info'} label="Info" />
				{eventOverview.game && (
					<>
						<TabButton
							icon={Trophy}
							onClick={() => setActiveTab('teams')}
							count={eventOverview.teams?.length}
							active={activeTab === 'teams'}
							label="Teams"
						/>
						{(eventOverview.game!.game.globalChat || eventOverview.game!.game.gameMasterChat) && (
							<TabButton
								icon={MessagesSquare}
								onClick={() => setActiveTab('chat')}
								count={unreadMessageCount}
								active={activeTab === 'chat'}
								label="Chat"
							/>
						)}
						{appConfig.permissions.allowGps && eventOverview.game!.game.showMap && (
							<TabButton icon={Map} onClick={() => setActiveTab('map')} active={activeTab === 'map'} label="Map" />
						)}
						<TabButton
							icon={ListCheck}
							onClick={() => setActiveTab('submissions')}
							count={
								eventOverview.taskSubmissions?.reduce((c, x) => {
									if (x.status == 'in-progress') c++;
									return c;
								}, 0) || undefined
							}
							active={activeTab === 'submissions'}
							label="Submissions"
						/>
						<TabButton
							icon={LockOpenIcon}
							onClick={() => setActiveTab('availability')}
							active={activeTab === 'availability'}
							label="Unlocks"
						/>
						<TabButton icon={GamepadIcon} onClick={() => setActiveTab('game')} active={activeTab === 'game'} label="Game" />
					</>
				)}
			</div>
			<>
				{activeTab == 'info' && (
					<EventInfo
						roundStates={eventOverview.gameRoundStates}
						event={eventOverview.event!}
						game={eventOverview.game!}
						skin={eventOverview.skin}
						variant={eventOverview.variant}
					/>
				)}
				{activeTab == 'teams' && (
					<Teams teams={eventOverview.teams} eventId={eventOverview.event!.id} eventState={eventOverview.event!.state} />
				)}
				{activeTab == 'chat' && (
					<FilePickerProvider startDirectory={directory!}>
						<ChatOverview
							chatMessages={eventOverview.chatMessages || []}
							teams={eventOverview.teams || []}
							eventId={eventOverview.event!.id}
							hasGameMasterChat={eventOverview.game!.game.gameMasterChat}
							hasGlobalChat={eventOverview.game!.game.globalChat}
							eventOverview={eventOverview}
						/>
					</FilePickerProvider>
				)}
				{activeTab == 'map' && <MapOverview eventOverview={eventOverview} />}
				{activeTab == 'submissions' && (
					<TaskSubmissions
						submissions={eventOverview.taskSubmissions!}
						teams={eventOverview.teams!}
						game={eventOverview.game!}
						eventOverview={eventOverview}
					/>
				)}
				{activeTab == 'availability' && (
					<>
						<RoundAvailability eventOverview={eventOverview} />
					</>
				)}
				{activeTab == 'game' && (
					<>
						<GameOverview eventOverview={eventOverview} />
					</>
				)}
			</>
		</div>
	);
};

const EventInfo = ({
	event,
	game,
	skin,
	variant,
	roundStates,
}: {
	event: NonNullable<EventOverview['event']>;
	game: EventOverview['game'];
	skin: EventOverview['skin'];
	variant: EventOverview['variant'];
	roundStates: EventOverview['gameRoundStates'];
}) => {
	const router = useRouter();
	const isAdmin = useIsAdmin();

	const startsAt = event.gameOverrides?.gameStartsByDate
		? new Date(event.gameOverrides.gameStartsByDate).toLocaleString('nl-NL', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
			})
		: '-';

	const statusInfo = {
		ended: `The game has ended. Press start to make the game available again.`,
		open: `The game is open, people can join but not yet play`,
		started: `The game has started. Check if all teams are logged into the app and review the assignments.`,
		draft: `The game hasn't started yet. Press start when the players are ready to log in to the app.`,
		archived: `The game is archived it cannot be started.`,
	} as const;

	const status = event?.status;
	const statusLookup = { ended: 'warn', open: 'info', started: 'success', draft: 'info' } as const;

	function StatusElement({ status, name }: { status: boolean; name: string }) {
		return (
			<div className="flex gap-2 font-semibold">
				{status ? <CheckSquare2 className="text-green-600" /> : <SquareX className="text-red-600" />}
				<span className="text-gray-700">{name}</span>
			</div>
		);
	}
	const LockAllRounds = async (lock = true) => {
		const rounds = game?.game.rounds;
		if (rounds) {
			await Promise.all(
				rounds.map((round) =>
					emitMessage({
						kind: 'event-change-round-unlock-global',
						roundId: round.id,
						eventId: event.id,
						unlocked: !lock,
					}),
				),
			);
		}
	};

	const SetFreezeMode = async (freeze = true) => {
		await emitMessage({
			kind: 'event-freeze',
			eventId: event.id,
			freeze: freeze,
		});
	};

	return (
		<AdminCard title="Event Info">
			<div className="flex flex-col gap-2 md:gap-6">
				<div className="flex flex-col gap-3 md:flex-row">
					<div className="flex flex-1 flex-col md:gap-3">
						<div>
							<span className="font-semibold text-gray-700">Event:</span> {event.name}{' '}
							{
								<Badge className="align-middle" status={statusLookup[status]}>
									{toSentenceCase(status)}
								</Badge>
							}
						</div>
						<div className="items-center gap-2">
							<span className="font-semibold text-gray-700">Game:</span> {game?.game.name ?? 'No game'}{' '}
							{isAdmin && game?.id && (
								<Link to="/dashboard/games/edit/$id" params={{ id: game?.id }}>
									<button className="items-center gap-1 rounded-full border border-gray-200 bg-gray-100 p-1 text-[10px] text-gray-900 hover:border-gray-300 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-500 dark:text-gray-50">
										<Wrench className="size-3" />
									</button>
								</Link>
							)}
						</div>
						<div className="flex items-center gap-2">
							<span className="font-semibold text-gray-700">Variant:</span> {variant?.name ?? 'Default game'}{' '}
							{isAdmin && event?.variantId && (
								<Link to="/dashboard/variants/edit/$id" params={{ id: event.variantId }}>
									<button className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 p-1 text-[10px] text-gray-900 hover:border-gray-300 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-500 dark:text-gray-50">
										<Wrench className="size-3" />
									</button>
								</Link>
							)}
						</div>
						<div>
							<span className="font-semibold text-gray-700">Language:</span> {game?.game.language}{' '}
							<img className="inline size-4" src={`/flags/${game?.game.language}.svg`} />
						</div>
						<div>
							<span className="font-semibold text-gray-700">Date:</span> {startsAt}
						</div>
						<div>
							<span className="font-semibold text-gray-700">Code:</span>{' '}
							{event.code || event.oldCode ? (
								<InlineCode clipboardButton={true} value={event.code ?? event.oldCode ?? undefined}>
									{event.code ?? event.oldCode}
								</InlineCode>
							) : (
								<small className="text-sm text-gray-500">Event has no code</small>
							)}
						</div>
						<div className="flex items-center gap-2">
							<span className="font-semibold text-gray-700">Show score:</span>
							<Toggle
								checked={!game!.game.hideScore}
								onChange={() => {
									emitMessage({ kind: 'event-set-hide-score', id: event.id, hideScore: !game!.game.hideScore });
								}}
							/>
						</div>
						{appConfig.permissions.allowSkins && (
							<div className="flex items-center gap-2">
								<span className="font-semibold text-gray-700">Skin:</span>
								<TinySkinPreview skin={skin ?? undefined} />
							</div>
						)}
					</div>

					<div className="mr-20 hidden flex-col gap-3 md:flex">
						{appConfig.permissions.allowGps && <StatusElement status={game!.game.showMap} name="Maps" />}
						<StatusElement status={!game!.game.hideScore} name="Show Score" />
						<StatusElement status={game!.game.globalChat} name="Global Chat" />
						<StatusElement status={game!.game.gameMasterChat} name="Game Master Chat" />
						<StatusElement status={game!.game.spectateFunction} name="Spectator Function" />
					</div>
				</div>

				<div>
					<span className="text-sm italic text-gray-600">{statusInfo[event.archived ? 'archived' : event.status]}</span>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					{!event.archived && (
						<>
							{(event.status == 'draft' || event.status == 'ended') && (
								<ModalButton
									title={`Are you sure you want to open the game?`}
									onAccept={async () => {
										const result = await emitMessage({ kind: 'event-set-status', id: event.id, status: 'open' });
										if (!result.success && result?.msg) {
											toast.error(result.msg);
										}
										if (result.success) {
											location.reload();
										}
									}}
									mode="primary"
									className="whitespace-nowrap"
								>
									<DoorOpen /> Open event
								</ModalButton>
							)}
							{event.status == 'open' && (
								<ModalButton
									title={`Are you sure you want to start the game?`}
									onAccept={async () => {
										const result = await emitMessage({ kind: 'event-set-status', id: event.id, status: 'started' });
										if (!result.success && result?.msg) {
											toast.error(result.msg);
										}
										if (result.success) {
											location.reload();
										}
									}}
									mode="primary"
									className="whitespace-nowrap"
								>
									<Play /> Start event
								</ModalButton>
							)}
							{event.status == 'started' && (
								<ModalButton
									title={`Are you sure you want to end the game?`}
									onAccept={async () => {
										await emitMessage({ kind: 'event-set-status', id: event.id, status: 'ended' });
									}}
									mode="primary"
									className="whitespace-nowrap"
								>
									<OctagonX /> End event
								</ModalButton>
							)}
							{roundStates?.some((round) => round.unlocked) && (
								<Button onClick={() => LockAllRounds()} mode={'inverse'} className="p-1 md:bg-gray-100">
									<LockIcon className="size-5" />
									Lock all rounds
								</Button>
							)}
							<Button
								onClick={() => SetFreezeMode(!event.state.frozen)}
								mode={'inverse'}
								// disabled={event.state.frozen}
								className="p-1 md:bg-gray-100"
							>
								{!event.state.frozen ? (
									<>
										<Snowflake className="size-5" /> Freeze Event
									</>
								) : (
									<>
										<Sun className="size-5" /> Unfreeze Event
									</>
								)}
							</Button>
						</>
					)}
					<Spacer />

					<Button to={'/dashboard/events/edit/$id'} params={{ id: event.id }} mode={'inverse'} className="p-1 md:bg-gray-100">
						<Pencil />
					</Button>

					{isAdmin && (
						<ModalButton
							title={`Are you sure you want to ${!event.archived ? 'archive' : 'unarchive'} "${event.name}"?`}
							onAccept={async () => {
								await emitMessage({ kind: 'event-archive', id: event.id, archive: !event.archived });
							}}
							mode={'secondary'}
							className="p-1 md:bg-gray-100"
						>
							{event.archived ? <ArchiveRestore /> : <Archive />}
						</ModalButton>
					)}

					{isAdmin && (
						<ModalButton
							title={`Are you sure you want to delete "${event.name}"?`}
							onAccept={async () => {
								await emitMessage({ kind: 'event-delete', id: event.id });
								router.navigate({ to: '/dashboard/events' });
							}}
							mode={'inverse'}
							className="p-1 md:bg-gray-100"
						>
							<Trash2 />
						</ModalButton>
					)}
				</div>
			</div>
		</AdminCard>
	);
};

const Teams = ({ teams, eventId, eventState }: { teams: EventOverview['teams']; eventId: string; eventState: EventState }) => {
	const sortedTeams = [...(teams || [])].sort((a, b) => b.score - a.score);

	// const [pointValues, setPointValues] = useState(sortedTeams.map((t) => ({ id: t.id, value: 0 })));

	const anyTeamHasMembers = teams?.some((t) => (t.data?.members?.length ?? 0) > 0) ?? false;

	return (
		<AdminCard title="Teams">
			<div className="flex flex-col gap-2">
				{sortedTeams.map((team, index) => {
					const ratId = team.data?.ratId;
					const captainId = team.captainId;
					const devices = team.data?.devices ?? [];
					const hasMultipleDevices = devices.length > 1;

					return (
						<div key={team.id} className="flex flex-col gap-2 border-b border-gray-100 py-2">
							<details className="flex flex-col">
								<summary className="flex cursor-pointer items-center justify-between" data-id={team.id}>
									<div className="flex items-center gap-2">
										<span className="block min-w-8 text-sm text-gray-600">#{index + 1}</span>
										<TeamAvatar avatar={team.data?.avatar} eventId={eventId} teamId={team.id} />
										<span className="text-sm font-semibold">{team.name}</span>
									</div>
									<div className="text-sm font-semibold tabular-nums text-gray-800">{team.score}</div>
								</summary>

								<div className="pl-[calc(2rem+2ch)]">
									<div className="mb-2 flex items-center justify-between">
										<div className="flex items-center gap-3 px-2 py-1">
											{team.rejoinCode && (
												<>
													Team Code:{' '}
													<InlineCode clipboardButton={true} value={team.rejoinCode}>
														{team.rejoinCode}
													</InlineCode>
												</>
											)}
										</div>
										<div className="flex items-center gap-2 font-semibold">
											<UpdateTeamPoints eventId={eventId} teamId={team.id} score={team.score} />
											<ModalButton
												title={`Are you sure you want to delete team "${team.name}"?`}
												onAccept={async () => {
													await emitMessage({ kind: 'team-delete', id: team.id, eventId });
												}}
												mode="secondary"
												size="sm"
												className="border-2 border-gray-200"
											>
												<Trash2 className="size-4" />
											</ModalButton>
										</div>
									</div>
									{anyTeamHasMembers && (
										<details className="flex flex-col">
											<summary className="text-xs font-semibold text-gray-700">Members</summary>

											<div className="mt-1 flex flex-col text-xs text-gray-600">
												{!team.data?.members && <small className="text-gray-400">No members</small>}

												{team.data?.members?.map((m) => {
													const isRat = m.id === ratId;
													return (
														<div
															key={m.id}
															className="group/member flex flex-col gap-1 py-1 sm:flex-row sm:items-center sm:justify-between"
														>
															<div className="flex items-center gap-2">
																<div className="p-1">{m.name}</div>
																{isRat && (
																	<span className="rounded bg-gray-200 px-2 py-0.5 text-[10px] uppercase tracking-wide">
																		Rat
																	</span>
																)}
															</div>

															{/* Keep rat button next to member; hover on desktop, always visible on small screens */}
															<div className="hidden gap-2 sm:flex sm:group-hover/member:flex">
																{!isRat && (
																	<button
																		onClick={() => {
																			emitMessage({
																				kind: 'event-change-rat',
																				eventId,
																				teamId: team.id,
																				ratId: m.id,
																			});
																		}}
																		className="rounded-full bg-gray-200 px-2 py-1 text-gray-900"
																	>
																		Make rat
																	</button>
																)}
															</div>
															<div className="flex gap-2 sm:hidden">
																{!isRat && (
																	<button
																		onClick={() =>
																			emitMessage({
																				kind: 'event-change-rat',
																				eventId,
																				teamId: team.id,
																				ratId: m.id,
																			})
																		}
																		className="rounded-full bg-gray-200 px-2 py-1 text-gray-900"
																	>
																		Make rat
																	</button>
																)}
															</div>
														</div>
													);
												})}
											</div>
										</details>
									)}

									{devices.length > 1 && (
										<details className="mt-2 flex flex-col">
											<summary className="cursor-pointer text-xs font-semibold text-gray-700">Devices</summary>

											<div className="mt-1 flex flex-col text-xs text-gray-600">
												{devices.map((d) => {
													const isCaptain = d.id === captainId;
													return (
														<div
															key={d.id}
															className="flex flex-col gap-1 py-1 sm:flex-row sm:items-center sm:justify-between"
														>
															<div className="flex items-center gap-2">
																<div className="p-1">{d.name?.trim() || d.id}</div>

																<span className="flex items-center gap-1 rounded bg-gray-200 px-2 py-0.5 text-xs uppercase tracking-wide">
																	{isCaptain ? (
																		<>
																			<Crown className="size-3" /> Captain
																		</>
																	) : (
																		<>
																			<Glasses className="size-3" /> Spectator
																		</>
																	)}
																</span>
															</div>

															{hasMultipleDevices && !isCaptain && (
																<div className="flex gap-2">
																	<button
																		onClick={() => {
																			emitMessage({
																				kind: 'event-change-captain',
																				eventId,
																				teamId: team.id,
																				captainId: d.id,
																			});
																		}}
																		className="rounded-full bg-gray-200 px-2 py-1 text-gray-900"
																	>
																		Make captain
																	</button>
																	<button
																		onClick={() => {
																			console.log('Deleting device ' + d.id);
																			// team.data.devices = team.data.devices.filter(
																			// 	(x) => x.id != d.id,
																			// );
																			emitMessage({
																				kind: 'event-remove-device',
																				eventId,
																				teamId: team.id,
																				deviceId: d.id,
																			});
																		}}
																		className="rounded-full bg-gray-200 px-2 py-1 text-gray-900"
																	>
																		<Trash2 className="size-3" />
																	</button>
																</div>
															)}
														</div>
													);
												})}
											</div>
										</details>
									)}
								</div>
							</details>
						</div>
					);
				})}
			</div>

			<div className="mt-5">
				<MediaArchiveView eventState={eventState} eventId={eventId} />
			</div>
		</AdminCard>
	);
};

const MediaArchiveView = ({ eventState, eventId }: { eventState: EventState; eventId: string }) => {
	const [archived, setArchived] = useState(eventState.mediaArchived == 'archived');
	const [isCreatingArchive, setIsCreatingArchive] = useState(false);

	const archiveUrl = `https://${import.meta.env.VITE_CDN_GAME}/${eventId}/media-archive.zip`;
	return (
		<div className="flex flex-col gap-2">
			{archived && (
				<div>
					<Para>Media archive available at the link below. </Para>
					<InlineCode clipboardButton={true} value={archiveUrl}>
						{archiveUrl}
					</InlineCode>
				</div>
			)}
			{!archived && <Para>Media not archived yet. you can create an archive by clicking the button below.</Para>}
			<Button
				mode="secondary"
				size="sm"
				disabled={isCreatingArchive}
				onClick={async () => {
					if (isCreatingArchive) return;
					setIsCreatingArchive(true);
					const res = await emitMessage({
						kind: 'event-download-media',
						eventId: eventId,
					});
					if (res.downloadUrl) {
						// window.open(res.downloadUrl, '_blank');
						toast.success(archived ? 'Media archive updated' : 'Media archive created');
						setIsCreatingArchive(false);
						setArchived(true);
					} else {
						toast.error(archived ? 'Failed to update media archive' : 'Failed to create media archive');
						setIsCreatingArchive(false);
					}
				}}
			>
				{isCreatingArchive ? (
					<Loader2 className="size-4 animate-spin" />
				) : (
					<>
						<DownloadIcon className="size-4" />
					</>
				)}
				{isCreatingArchive && (archived ? 'Updating archive...' : 'Creating archive...')}
				{!isCreatingArchive && (archived ? 'Update archive' : 'Create archive')}
			</Button>
		</div>
	);
};

const updateTeamPointsSchema = z.object({
	points: z.number(),
});

type UpdateTeamPointsForm = z.infer<typeof updateTeamPointsSchema>;

const UpdateTeamPoints = ({ eventId, teamId, score }: { eventId: string; teamId: string; score: number }) => {
	const methods = useForm<UpdateTeamPointsForm>({
		resolver: zodResolver(updateTeamPointsSchema),
		defaultValues: {
			points: 0,
		},
	});

	const onSubmit = (data: UpdateTeamPointsForm) => {
		console.log('Submitting points:', data.points);
		if (typeof data.points === 'number' && data.points !== 0) {
			emitMessage({ kind: 'update-team-points', eventId, teamId, points: data.points });
		}
		methods.reset();
	};
	return (
		<div className="flex flex-row items-center gap-2">
			{score}
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-row items-center gap-2">
					<NumberField className="-mt-1 w-16" fieldSize="sm" name="points" label="" hideLabel={true} />
					<Button type="submit" size="sm">
						+/-
					</Button>
					<Tooltip
						align="right"
						info="Award teams points by entering a number here. You can give penalty points by entering a negative number."
					/>
				</form>
			</FormProvider>
		</div>
	);
};

const taskSubmissionSchema = z.object({
	pointsGiven: z.number(),
	completionResponse: z.string().nullish(),
});

type TaskSubmissionForm = z.infer<typeof taskSubmissionSchema>;

type TaskSubmissionProps = {
	submission: {
		taskId: string;
		teamName: string;
		teamId: string;
		eventId: string;
		answer: TaskAnswer;
		pointsGiven: number;
		attempts: number;
		status: TaskStatus;
		completionResponse: string;
		updatedAt: string;
	};
	task: GameTask;
	readonly?: boolean;
	teams: TeamRecord[];
};

const TaskSubmission = ({ submission, task, readonly, teams }: TaskSubmissionProps) => {
	const methods = useForm<TaskSubmissionForm>({
		resolver: zodResolver(taskSubmissionSchema),
		defaultValues: {
			pointsGiven: submission.status === 'done' ? submission.pointsGiven : task.points,
			completionResponse: submission.completionResponse
				? submission.completionResponse
				: task.defaultCompletionResponse
					? task.defaultCompletionResponse
					: '',
		},
	});
	const [canEdit, setCanEdit] = useState(!readonly);
	const onSubmit = (data: TaskSubmissionForm) => {
		if (!data.completionResponse) {
			if (data.pointsGiven <= 0) {
				data.completionResponse = task.defaultCompletionResponseWrong;
			} else {
				data.completionResponse = task.defaultCompletionResponse;
			}
		}

		emitMessage({
			kind: 'event-task-review-submission',
			eventId: submission.eventId,
			teamId: submission.teamId,
			taskId: submission.taskId,
			pointsGiven: data.pointsGiven,
			completionResponse: data.completionResponse,
		});
	};

	function iconForTask(task: GameTask): LucideIcon {
		switch (task.type) {
			case 'augmented-reality':
				return Rotate3D;
			case 'information':
				return Info;
			case 'automatic-validation':
				switch (task.typeAutomaticValidationConfig!.kind) {
					case 'open':
						return NotebookPen;
					case 'multiple-choice':
						return ListChecks;
					case 'multi-choice':
						return ListChecks;
					default:
						unreachable(task.typeAutomaticValidationConfig!.kind);
				}
				break;
			case 'validation-by-game-master':
				switch (task.typeGameMasterValidationConfig!.kind) {
					case 'open':
						return NotebookPen;
					case 'photo':
						return Camera;
					case 'video':
						return Video;
					default:
						unreachable(task.typeGameMasterValidationConfig!.kind);
				}
				break;
			case 'fullscreen-video':
				return Video;
			case 'bingo':
				return FerrisWheel;
			default:
				unreachable(task.type);
		}
	}

	function formatUpdatedAt(isoDateString: string) {
		const date = new Date(isoDateString);
		const now = new Date();
		const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();

		if (isToday) {
			return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		} else {
			return date.toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
		}
	}
	// cspell:disable-next-line
	const defaultCompletionResponse = task.defaultCompletionResponse || 'Goed antwoord';
	// cspell:disable-next-line
	const defaultCompletionResponseWrong = task.defaultCompletionResponseWrong || 'Fout antwoord';

	const [open, setOpen] = useState(!readonly);
	const [showQuestion, setShowQuestion] = useState(false);
	return (
		<div className="mb-2 rounded-md border border-gray-100 bg-white p-3 shadow-sm">
			{/* <-- Head --> */}
			<div className="flex cursor-pointer items-center gap-1" onClick={() => setOpen(!open)}>
				<TeamAvatar
					avatar={teams.find((t) => t.id === submission.teamId)?.data.avatar}
					eventId={submission.eventId}
					teamId={submission.teamId}
				/>
				<div className={`flex font-semibold`}></div>

				<div className="truncate">
					<div className="flex flex-row gap-1 truncate">
						<span className="font-semibold">{submission.teamName}: </span>
						<span className="truncate">
							{task.title}
							{task.attempts > 1 || task.attempts == 0 ? ` - ${submission.attempts + 1}/${task.attempts}` : ''}
						</span>
					</div>
					<div className="text-xs md:hidden">{formatUpdatedAt(submission.updatedAt)}</div>
				</div>
				<Spacer />
				<Badge
					status={
						submission.status == 'done' && task.type !== 'information'
							? submission.pointsGiven > 0
								? 'success'
								: 'danger'
							: 'info'
					}
					className="hidden md:flex"
				>
					{task.type === 'information'
						? 'Read'
						: `${submission.status == 'done' ? `${submission.pointsGiven}/` : ''}${task.points} points`}
				</Badge>
				<div className="hidden md:flex">{formatUpdatedAt(submission.updatedAt)}</div>
				<div className="hidden size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100 md:flex">
					<Icon className="size-5 text-gray-400 opacity-90" icon={iconForTask(task)} />
				</div>

				<ChevronUp className={`mr-2 size-6 shrink-0 cursor-pointer transition-transform ${open ? '' : 'rotate-180'}`} />
			</div>
			{/* <-- Body --> */}
			<div className={`overflow-hidden transition-all duration-300 ${open ? 'h-fit opacity-100' : 'max-h-0 opacity-0'} `}>
				{task.type !== 'information' && (
					<div className="mt-2 flex flex-col-reverse gap-2 md:flex-row">
						<div className="w-full flex-1 md:basis-2/3">
							{task.gameMasterInstructions &&
								(task.type === 'bingo' ||
									task.typeGameMasterValidationConfig!.kind === 'video' ||
									task.typeGameMasterValidationConfig!.kind === 'photo') && (
									<div className="gap-1 rounded-md bg-gray-200 p-3 shadow-sm">
										<span className="inline font-semibold">Instructions: </span>
										<RichTextContent className="!inline !p-0" content={task.gameMasterInstructions} />
									</div>
								)}
							<FormProvider {...methods}>
								<form onSubmit={methods.handleSubmit(onSubmit)} className="mt-2 flex flex-col gap-2">
									<TextField name="completionResponse" label={'Response'} placeholder="Response"></TextField>
									<div className="flex gap-1">
										{
											<div
												onClick={() => {
													methods.setValue('completionResponse', defaultCompletionResponse);
													methods.setValue('pointsGiven', task.points);
												}}
											>
												<Badge
													status="success"
													className="w-fit cursor-pointer rounded-xl p-1 text-sm md:rounded-full"
												>
													{defaultCompletionResponse}
													<PencilLine className="inline size-3" />
												</Badge>
											</div>
										}
										{
											<div
												onClick={() => {
													methods.setValue('completionResponse', defaultCompletionResponseWrong);
													methods.setValue('pointsGiven', 0);
												}}
											>
												<Badge
													status="danger"
													className="w-fit cursor-pointer rounded-xl p-1 text-sm md:rounded-full"
												>
													{defaultCompletionResponseWrong}
													<PencilLine className="inline size-3" />
												</Badge>
											</div>
										}
									</div>
									<NumberField name="pointsGiven" label="Your review:" />

									<div className="flex items-center gap-1">
										{readonly ? (
											<Button
												type="submit"
												onClick={() => {
													const values = methods.getValues();
													methods.handleSubmit(() => onSubmit(values))();
													if (canEdit) setCanEdit(false);
												}}
											>
												Change
											</Button>
										) : (
											<>
												<Button
													type="submit"
													onClick={() => {
														const values = methods.getValues();
														methods.handleSubmit(() => onSubmit(values))();
														if (canEdit) setCanEdit(false);
													}}
												>
													Accept
												</Button>
												<Button
													type="button"
													mode="secondary"
													onClick={() => {
														const values = methods.getValues();
														methods.handleSubmit(() =>
															onSubmit({
																...values,
																pointsGiven: Math.min(0, values.pointsGiven),
															}),
														)();
														if (canEdit) setCanEdit(false);
													}}
												>
													<span className="hidden md:flex">Decline</span>
													<span className="flex md:hidden">X</span>
												</Button>
												<Tooltip info="Press decline to reject the assignment; the player will not receive any points. Note: the client will still receive your response." />
											</>
										)}
										<Spacer />
										<span className="truncate">Show question</span>
										<Toggle
											title="Show question"
											checked={showQuestion}
											onChange={() => setShowQuestion(!showQuestion)}
										/>
									</div>
								</form>
							</FormProvider>
						</div>
						<div className="flex flex-1 flex-col-reverse gap-1 md:basis-1/3">
							{task.gameMasterInstructions
								? ['augmented-reality', 'fullscreen-video', 'automatic-validation'].includes(task.type) ||
									(task.type === 'validation-by-game-master' && task.typeGameMasterValidationConfig!.kind === 'open' && (
										<div className="flex-1 rounded-md bg-gray-200 p-3 shadow-sm md:min-h-20">
											<span className="inline py-2 font-semibold">Instructions: </span>
											<RichTextContent className="inline !pl-0" content={task.gameMasterInstructions} />
										</div>
									))
								: task.type === 'automatic-validation' && (
										<div className="flex-1 rounded-md bg-gray-200 p-3 shadow-sm md:min-h-20">
											<span className="inline py-2 font-semibold">Possible answer: </span>
											{task.typeAutomaticValidationConfig!.kind === 'open' ? (
												<span>{task.typeAutomaticValidationConfig!.openAnswers?.[0]?.name ?? ''}</span>
											) : task.typeAutomaticValidationConfig!.kind === 'multi-choice' ||
											  task.typeAutomaticValidationConfig!.kind === 'multiple-choice' ? (
												<span>
													{task
														.typeAutomaticValidationConfig!.multipleChoiceAnswers?.map((a) => a.name)
														.join(', ') ?? ''}
												</span>
											) : (
												''
											)}
										</div>
									)}
							<div className="flex-1 rounded-md md:min-h-20">
								<TaskAnswerPreview
									teamId={submission.teamId}
									eventId={submission.eventId}
									answer={submission.answer}
									task={task}
								/>
							</div>
						</div>
					</div>
				)}
				<div
					className={`overflow-hidden transition-all duration-300 ${(open && showQuestion) || (open && task.type === 'information') ? 'h-fit opacity-100' : 'max-h-0 opacity-0'} `}
				>
					<div className="mt-2 flex flex-col gap-2 md:flex-row">
						<div className="flex-1 rounded-md bg-gray-200 p-3 shadow-sm md:min-h-20 md:basis-2/3">
							<div className="text-lg font-semibold">{task.title}</div>
							<div className="font-semibold">{task.subtitle}</div>
							{task.description && <RichTextContent className="!pl-0" content={task.description} />}
							<div className="flex flex-col gap-1">
								{(submission.answer.kind == 'single-choice' || submission.answer.kind == 'multiple-choice') &&
									(() => {
										const multipleChoiceAnswers = task.typeAutomaticValidationConfig?.multipleChoiceAnswers;
										const ids =
											submission.answer.kind == 'single-choice'
												? [submission.answer.chosenId]
												: submission.answer.chosenIds;
										if (multipleChoiceAnswers && multipleChoiceAnswers.length > 0)
											return multipleChoiceAnswers.map((c) => (
												<div
													className={`flex w-full items-center rounded-md border-2 border-white p-4 text-black ${ids.includes(c.id) ? 'bg-white' : ''}`}
												>
													{c.name}
													{c.correct && '(correct answer)'}
												</div>
											));
									})()}
							</div>
						</div>
						{task.media && (
							<div className="flex flex-1 flex-col-reverse gap-1 md:basis-1/3">
								<div className="flex-1 rounded-md shadow-sm md:min-h-20">
									<TaskQuestionPreview node={task.media} />
								</div>
							</div>
						)}
					</div>
				</div>
				<div>
					{task.type == 'information' && task.gameMasterInstructions && (
						<div className="flex-1 rounded-md bg-gray-200 p-3 shadow-sm md:min-h-20">
							<span className="inline py-2 font-semibold">Instructions: </span>
							<RichTextContent className="inline !pl-0" content={task.gameMasterInstructions} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
const TaskSubmissions = ({
	submissions,
	game,
	teams,
	eventOverview,
}: {
	submissions: TaskStateRecord[];
	teams: TeamRecord[];
	game: GameRecord;
	eventOverview: EventOverview;
}) => {
	// const submissionsToReview = submissions?.filter((s) => s.status == 'in-progress') ?? [];
	const initialSubmissions = submissions?.filter((s) => s.status !== 'todo') || [];
	const [submissionsToReview, setSubmissionsToReview] = useState(initialSubmissions);

	const tasks = eventOverview.game?.game.rounds.flatMap((round) => round.tasks) ?? [];
	const rounds = eventOverview.game?.game.rounds ?? [];

	return (
		<AdminCard title="Task Submissions">
			<FilterControls
				items={initialSubmissions || []}
				onChanged={setSubmissionsToReview}
				filterOptions={[
					{
						allowAll: false,
						default: 'in-progress',
						key: 'status',
						label: 'Status',
						values: [
							{ label: 'In progress', value: 'in-progress', selected: true },
							{ label: 'Done', value: 'done' },
						],
						getValue: (e) => e.status,
					},
					{
						key: 'team',
						label: 'Team',
						getValue: (e) => e.teamId!,
						values: teams?.map((team) => ({ value: team.id, label: team.name })) || [],
					},
					{
						key: 'round',
						label: 'Round',
						getValue: (e) => rounds.find((r) => r.tasks.find((t) => t.id == e.taskId))?.id ?? '',
						values: rounds.map((sb) => ({ value: sb.id, label: sb.title })),
					},
					{
						key: 'task',
						label: 'Task',
						getValue: (e) => e.taskId,
						values: tasks.map((sb) => ({ value: sb.id, label: sb.title })),
					},
				]}
			/>
			{submissionsToReview.length > 0 ? (
				<>
					<Para>The following tasks must be reviewed manually: </Para>
					<div className="mt-4">
						{submissionsToReview
							.filter((s) => s.answer !== undefined)
							.map((submission) => {
								const teamName = teams.find((t) => t.id == submission.teamId)?.name ?? 'Unknown team';
								// Find the task in the game's rounds
								const task = game.game.rounds.flatMap((round) => round.tasks).find((t) => t.id === submission.taskId);
								if (!task) return null;
								return (
									<TaskSubmission
										key={submission.taskId + '-' + submission.teamId}
										// @ts-expect-error error because answer is mandatory, but we filtered above.
										submission={{ ...submission, teamName }}
										task={task}
										readonly={submission.status == 'done'}
										teams={teams}
									/>
								);
							})}
					</div>
				</>
			) : (
				submissionsToReview.length == 0 && <Para>No submissions to review.</Para>
			)}
		</AdminCard>
	);
};

const RoundAvailability = ({ eventOverview }: { eventOverview: EventOverview }) => {
	const rounds = eventOverview.game?.game.rounds ?? [];
	const roundStates = eventOverview.gameRoundStates ?? [];

	// const filterStates = (states: GameRound[]) => states.filter((round) => !roundStates.find((r) => r.roundId == round.id)?.unlocked);
	const filterStates = (states: GameRound[]) => states;
	const initialRounds = filterStates(rounds);

	const [visibleRounds, setVisibleRounds] = useState(initialRounds);

	const LockAllRounds = async (lock = true) => {
		await Promise.all(
			rounds.map((round) =>
				emitMessage({
					kind: 'event-change-round-unlock-global',
					roundId: round.id,
					eventId: eventOverview.event!.id!,
					unlocked: !lock,
				}),
			),
		);
	};

	const SetFreezeMode = async (freeze = true) => {
		await emitMessage({
			kind: 'event-freeze',
			eventId: eventOverview.event!.id,
			freeze: freeze,
		});
	};

	return (
		<AdminCard title="Rounds">
			<FilterControls
				items={filterStates(initialRounds)}
				getSearchableText={(s) => s.title}
				onChanged={setVisibleRounds}
				sortOptions={[
					{
						key: 'order',
						label: 'Order',
						sortFn: (_a, _b) => 1,
					},
					{
						key: 'name',
						label: 'Name',
						sortFn: (a, b) => a.title.localeCompare(b.title),
					},
				]}
				filterOptions={[
					{
						key: 'availability',
						values: GameRoundAvailabilityOptions,
						getValue: (round) => round.availability,
					},
					// {
					// 	key: 'unlocked',
					// 	values: [true, false].map((v) => ({ label: `${v}`, value: `${v}` })),
					// 	getValue: (round) => `${roundStates.find((s) => s.roundId === round.id)!.unlocked}`,
					// },
				]}
			/>
			<div className="flex h-10 justify-end gap-2 px-2">
				{roundStates?.some((round) => round.unlocked) && (
					<Button onClick={() => LockAllRounds()} mode={'inverse'} className="p-1 md:bg-gray-100">
						<LockIcon className="size-5" />
						Lock all rounds
					</Button>
				)}
				<Button
					onClick={() => SetFreezeMode(!eventOverview.event!.state.frozen)}
					mode={'inverse'}
					// disabled={eventOverview.event!.state.frozen}
					className="p-1 md:bg-gray-100"
				>
					{!eventOverview.event!.state.frozen ? (
						<>
							<Snowflake className="size-5" /> Freeze Event
						</>
					) : (
						<>
							<Sun className="size-5" /> Unfreeze Event
						</>
					)}
				</Button>
			</div>
			{visibleRounds.length === 0 ? (
				<div className="mt-4">
					<Para>No rounds found.</Para>
				</div>
			) : (
				<div className="mt-4 flex flex-col">
					{visibleRounds.map((r) => {
						const index = rounds.findIndex((round) => round.id === r.id);
						const roundState = roundStates.find((s) => s.roundId === r.id && s.teamId === null)!;
						return <RoundSummary key={r.id} round={r} index={index} roundState={roundState} eventOverview={eventOverview} />;
					})}
				</div>
			)}
		</AdminCard>
	);
};

const RoundSummary = ({
	round,
	index,
	roundState,
	eventOverview,
}: {
	round: GameRound;
	index: number;
	roundState: RoundState;
	eventOverview: EventOverview;
}) => {
	const { showModal } = useModal();

	const isGloballyUnlocked = roundState?.unlocked ?? false;

	const availabilityLabel = GameRoundAvailabilityOptions.find((a) => a.value === round.availability)?.label;

	const { taskCount, tasksCompletedCount } = getRoundProgress(eventOverview, round.id);

	const changeAvailability = async () => {
		console.log('changeAvailability', round.id, !isGloballyUnlocked, eventOverview.event!.id);

		await emitMessage({
			kind: 'event-change-round-unlock-global',
			roundId: round.id,
			eventId: eventOverview.event!.id!,
			unlocked: !isGloballyUnlocked,
		});
		// TODO: update state locally so the button instantly disappears

		// const roundState = roundStates.find((s) => s.roundId === roundId)
		// if (roundState) roundState.unlocked = true;
		// setToggleAbleRounds(filterStates(toggleAbleRounds));
	};

	return (
		<details className="group rounded-md">
			<summary className="flex items-center gap-3 bg-gray-50 px-2 py-1 hover:bg-gray-100">
				<ChevronRight className="size-5 text-gray-500 group-open:rotate-90" />
				<span className="select-none font-semibold">
					#{index + 1} - {round.title}
				</span>
				<small className="select-none text-gray-500">{availabilityLabel}</small>
				{round.availability === 'time-minutes' && eventOverview.event?.state?.startedAt && (
					<TimeUnlockBadge
						unlockTime={
							new Date(new Date(eventOverview.event?.state.startedAt).getTime() + round.availabilityTimeValue * 60 * 1000)
						}
					/>
				)}
				<Spacer />
				<ProgressIndicator count={tasksCompletedCount} total={taskCount} />
				<div className="flex items-center gap-3">
					<button
						onClick={() => {
							showModal(<TeamRoundUnlockModal round={round} eventOverview={eventOverview} />);
						}}
					>
						<UserLock className="size-4" />
					</button>
					<LockToggle onClick={changeAvailability} isLocked={!isGloballyUnlocked} />
				</div>
			</summary>
			<div className="pl-6">
				{round.tasks.map((t) => {
					return <TaskSummary key={t.id} task={t} index={index} eventOverview={eventOverview} />;
				})}
			</div>
		</details>
	);
};

function LockToggle({ onClick, isLocked }: { onClick: () => void; isLocked: boolean }) {
	return (
		<button
			onClick={onClick}
			title={!isLocked ? 'Unlocked, click to lock' : 'Locked, click to unlock'}
			className={`h-6.5 relative inline-flex w-10 items-center rounded-full transition-colors duration-200 focus:outline-none ${
				!isLocked ? 'border border-success-500 bg-success-400' : 'border border-gray-400 bg-gray-300'
			}`}
		>
			<span
				className={`flex size-6 items-center justify-center rounded-full border bg-white p-0.5 transition-transform duration-200 ${
					!isLocked ? 'translate-x-4 border-success-500' : 'border-gray-400'
				}`}
			>
				<Icon icon={!isLocked ? LockOpenIcon : LockIcon} className={`size-4 text-gray-700`} />
			</span>
		</button>
	);
}

function TeamRoundUnlockModal({ round, eventOverview }: { round: GameRound; eventOverview: EventOverview }) {
	const { cancelModal } = useModal();

	const [teamStates, setTeamStates] = useState<RoundState[]>(eventOverview?.gameRoundStates?.filter((s) => s.roundId === round.id) ?? []);

	const changeAvailabilityTeam = async (teamId: string, isUnlocked: boolean) => {
		console.log('changeAvailabilityTeam', round.id, teamId, !isUnlocked, eventOverview.event!.id);

		emitMessage({
			kind: 'event-change-round-unlock',
			roundIds: [round.id],
			teamId: teamId,
			eventId: eventOverview.event!.id!,
			unlocked: !isUnlocked,
		});

		setTeamStates(teamStates.map((s) => (s.teamId === teamId ? { ...s, unlocked: !isUnlocked } : s)));
	};

	return (
		<Modal title={`${round.title} - ${round.availability}`} className="max-h-full overflow-auto">
			<div className="flex flex-col gap-4">
				<Para>Change availability per team:</Para>
				<div className="flex flex-col overflow-hidden rounded-md border border-gray-200">
					<div className="overflow-y-auto">
						{eventOverview?.teams?.map((t) => {
							const teamState = teamStates.find((s) => s.teamId === t.id);
							const isUnlocked = teamState?.unlocked ?? false;
							return (
								<div className="flex items-center justify-between bg-gray-50 px-2 py-1 hover:bg-gray-100" key={t.id}>
									<span>{t.name}</span>
									<LockToggle onClick={() => changeAvailabilityTeam(t.id, isUnlocked)} isLocked={!isUnlocked} />
								</div>
							);
						})}
					</div>
				</div>
				<Button className="ml-auto" mode="secondary" onClick={() => cancelModal()}>
					Done
				</Button>
			</div>
		</Modal>
	);
}

function ProgressIndicator({ count, total }: { count: number; total: number }) {
	if (count === total && total > 0)
		return (
			<span className="flex items-center gap-1 text-green-500">
				<Check className="size-4" /> {count}/{total}
			</span>
		);
	return (
		<span className="text-gray-500">
			{count}/{total}
		</span>
	);
}

function getTaskProgress(eventOverview: EventOverview, taskId: string) {
	const taskStates = eventOverview.taskSubmissions?.filter((s) => s.taskId === taskId) ?? [];
	const teamCount = eventOverview.teams?.length ?? 0;
	const teamsAnsweredCount = taskStates.filter((s) => s.status === 'done' && s.teamId).length;

	return {
		teamCount,
		teamsAnsweredCount,
	};
}

function getRoundProgress(eventOverview: EventOverview, roundId: string) {
	const round = eventOverview.game?.game.rounds.find((r) => r.id === roundId);
	const tasks = round?.tasks ?? [];

	const taskCount = tasks.length;
	const tasksCompletedCount = tasks
		.map((t) => getTaskProgress(eventOverview, t.id))
		.reduce((acc, curr) => {
			const done = curr.teamsAnsweredCount === curr.teamCount;
			return acc + (done ? 1 : 0);
		}, 0);

	return {
		taskCount,
		tasksCompletedCount,
	};
}

const TaskSummary = ({ task, index, eventOverview }: { task: GameTask; index: number; eventOverview: EventOverview }) => {
	const taskStates = eventOverview.taskSubmissions?.filter((s) => s.taskId === task.id) ?? [];
	const isGloballyUnlocked = taskStates.find((s) => s.teamId === null)?.unlocked ?? false;

	const { teamCount, teamsAnsweredCount } = getTaskProgress(eventOverview, task.id);

	// TODO: have a task state with null team id
	// this would be the global task state
	// when toggling the global state the backend should update all the team states to the same value
	// and when a new team joins it copies the global state

	const changeAvailabilityGlobal = async () => {
		console.log('changeAvailability', task.id, !isGloballyUnlocked, eventOverview.event!.id);

		await emitMessage({
			kind: 'event-change-task-unlock-global',
			taskId: task.id,
			eventId: eventOverview.event!.id!,
			unlocked: !isGloballyUnlocked,
		});
	};

	const changeAvailability = async (taskId: string, teamId?: string) => {
		const taskState = taskStates.find((s) => s.taskId === taskId && (teamId ? s.teamId === teamId : true));
		if (!taskState) return;
		const isUnlocked = taskState.unlocked;

		console.log('changeAvailability', taskId, teamId, !isUnlocked, eventOverview.event!.id);
		console.log('taskState', taskState);

		await emitMessage({
			kind: 'event-change-task-unlock',
			eventId: eventOverview.event!.id!,
			teamId: teamId ?? 'global',
			taskIds: [taskId],
			unlocked: !isUnlocked,
		});
	};

	return (
		<details>
			<summary className="flex items-center gap-3 bg-gray-50 px-2 py-1 hover:bg-gray-100">
				<ChevronRight className="size-5 text-gray-500 group-open:rotate-90" />
				<span className="select-none font-semibold">
					#{index + 1} - {task.title}
				</span>
				<small className="select-none text-gray-500">{task.availability}</small>
				<Spacer />
				<ProgressIndicator count={teamsAnsweredCount} total={teamCount} />
				<button
					onClick={() => changeAvailabilityGlobal()}
					title={isGloballyUnlocked ? 'Unlocked, click to lock' : 'Locked, click to unlock'}
					className={`h-6.5 relative inline-flex w-10 items-center rounded-full transition-colors duration-200 focus:outline-none ${
						isGloballyUnlocked ? 'border border-success-500 bg-success-400' : 'border border-gray-400 bg-gray-300'
					}`}
				>
					<span
						className={`flex size-6 items-center justify-center rounded-full border bg-white p-0.5 transition-transform duration-200 ${
							isGloballyUnlocked ? 'translate-x-4 border-success-500' : 'border-gray-400'
						}`}
					>
						<Icon icon={isGloballyUnlocked ? LockOpenIcon : LockIcon} className={`size-4 text-gray-700`} />
					</span>
				</button>
			</summary>
			<div className="pl-6">
				{taskStates
					.filter((s) => s.teamId !== null)
					.map((s) => {
						const team = eventOverview.teams?.find((t) => t.id === s.teamId);
						return (
							<details key={s.teamId}>
								<summary key={s.teamId} className="flex items-center gap-2 bg-gray-50 px-2 py-1 hover:bg-gray-100">
									<span>{team?.name}:</span>
									<TinyAnswerPreview task={task} state={s} />
									<Spacer />
									<div>
										<button
											onClick={() => changeAvailability(task.id, s.teamId!)}
											title={s.unlocked ? 'Unlocked, click to lock' : 'Locked, click to unlock'}
											className={`h-6.5 relative inline-flex w-10 items-center rounded-full transition-colors duration-200 focus:outline-none ${
												s.unlocked
													? 'border border-success-500 bg-success-400'
													: 'border border-gray-400 bg-gray-300'
											}`}
										>
											<span
												className={`flex size-6 items-center justify-center rounded-full border bg-white p-0.5 transition-transform duration-200 ${
													s.unlocked ? 'translate-x-4 border-success-500' : 'border-gray-400'
												}`}
											>
												<Icon icon={s.unlocked ? LockOpenIcon : LockIcon} className={`size-4 text-gray-700`} />
											</span>
										</button>
									</div>
								</summary>
								<div className="pl-6">
									{s.answer && (
										<TaskAnswerPreview
											answer={s.answer}
											teamId={s.teamId!}
											eventId={eventOverview.event!.id!}
											task={task}
										/>
									)}
								</div>
							</details>
						);
					})}
			</div>
		</details>
	);
};

const TinyAnswerPreview = ({ task, state }: { task: GameTask; state: TaskState }) => {
	const answer = state.answer;

	const sanitizeAnswer = (input: string) => input.toLowerCase().replace(/\s+/g, ''); //Remove all whitespaces
	const statusView = <small className="text-gray-500">{state.status}</small>;

	// if done only show the answer in most cases, otherwise show the status
	const maybeStatusView = state.status !== 'done' ? statusView : null;

	const correctAnswers = task.typeAutomaticValidationConfig?.openAnswers?.map((a) => a.name) ?? [];

	let userAnswer = '';
	let statusColor;
	let question;

	if (!answer) return statusView;

	if (task.typeGameMasterValidationConfig?.kind == 'open' && answer.kind == 'text') {
		userAnswer = sanitizeAnswer(answer.text);
		if (state.status == 'done') {
			if (state.pointsGiven == task.points) {
				statusColor = 'bg-success-400'; // green
			} else if (state.pointsGiven < task.points) {
				statusColor = 'bg-red-400'; // red}
			}
		} else {
			statusColor = 'bg-gray-400'; // gray
		}
		return (
			<div className="flex items-center gap-2 rounded-md bg-white p-2 shadow-sm">
				<span>{userAnswer}</span>
				<span className={`flex size-3 rounded-full ${statusColor}`}></span>
			</div>
		);
	}

	switch (answer.kind) {
		case 'text': {
			userAnswer = sanitizeAnswer(answer.text);

			if (task.type === 'information' || task.type === 'fullscreen-video') return statusView;
			const closestAnswer = closest(userAnswer, correctAnswers.map(sanitizeAnswer));

			const answerCorrect = distance(userAnswer, closestAnswer) <= (task.typeAutomaticValidationConfig?.openMarginForError || 0);

			// determine correctness color
			if (answerCorrect) {
				statusColor = 'bg-success-400'; // green
			} else {
				statusColor = 'bg-red-400'; // red
			}
			return (
				<div className="flex items-center gap-2 rounded-md bg-white p-2 shadow-sm">
					<span>{answer.text}</span>
					<span className={`flex size-3 rounded-full ${statusColor}`}></span>
				</div>
			);
			break;
		}
		case 'single-choice':
			question = task.typeAutomaticValidationConfig?.multipleChoiceAnswers?.find((a) => a.id === answer.chosenId);
			return (
				<>
					<div className={`flex items-center gap-2 rounded-md bg-white p-2 shadow-sm`}>
						{question?.name ?? answer.chosenId}{' '}
						<span className={`flex size-3 rounded-full ${question?.correct ? 'bg-succes-400' : 'bg-red-400'}`}></span>
					</div>
					{maybeStatusView}
				</>
			);
		case 'multiple-choice':
			return (
				<>
					<div className="flex flex-col gap-1">
						{answer.chosenIds.map((id) => {
							const submission = task.typeAutomaticValidationConfig?.multipleChoiceAnswers?.find((a) => a.id === id);

							return (
								<div key={id} className="flex items-center gap-2 rounded-md bg-white p-2 shadow-sm">
									{submission?.name ?? id}
									<span
										className={`flex size-3 rounded-full ${submission?.correct ? 'bg-success-400' : 'bg-red-400'}`}
									></span>
								</div>
							);
						})}
					</div>
					{maybeStatusView}
				</>
			);
		case 'video':
			return (
				<>
					<div>{answer.filename}</div>
					{maybeStatusView}
				</>
			);
		case 'photo':
			return (
				<>
					<div>{answer.filename}</div>
					{maybeStatusView}
				</>
			);
		default:
			return null;
	}
};

const formatDate = (d: number) => {
	const date = new Date(d);
	const now = new Date();

	const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();

	return date.toLocaleString(
		'en-GB',
		isToday
			? { hour: '2-digit', minute: '2-digit', hour12: false }
			: {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: false,
				},
	);
};

const MapOverview = ({ eventOverview }: { eventOverview: EventOverview }) => {
	const teams = eventOverview.teams ?? [];
	const eventId = eventOverview.event?.id;

	const gameRounds = eventOverview.game?.game.rounds;
	const roundMarkers: TaskOrRoundMarker[] =
		gameRounds
			?.filter((r) => r.availability === 'gps')
			.map((round) => {
				return {
					roundId: round.id,
					round: round,
					lat: round.availabilityGpsConfig?.lat ?? 0,
					lng: round.availabilityGpsConfig?.lng ?? 0,
					radius: round.availabilityGpsConfig?.radius ?? 40,
					displayName: round.title,
				};
			}) ?? [];

	const taskMarkers: TaskMarker[] =
		gameRounds
			?.flatMap((r) => r.tasks)
			.filter((t) => t.availability === 'gps')
			.map((t) => {
				return {
					taskId: t.id,
					task: t,
					lat: t.availabilityGpsConfig?.lat ?? 0,
					lng: t.availabilityGpsConfig?.lng ?? 0,
					radius: t.availabilityGpsConfig?.radius ?? 40,
					displayName: t.title,
				};
			}) ?? [];

	const markers = [...roundMarkers, ...taskMarkers];

	const playerLocations = teams
		.filter((t) => t.data)
		.map((team) => ({
			id: team.id,
			name: team.name,
			lat: team.data?.lat ?? 0,
			lng: team.data?.lng ?? 0,
			heading: team.data?.heading ?? null,
			lastUpdated: formatDate(team.data.timestamp),
			avatarUrl: team.data?.avatar ? getAvatarUrl(eventId!, team.id, team.data.avatar) : undefined,
		}));

	const ChannelButton = ({ label, channelId, team }: { label: string; channelId: string; team?: TeamRecord }) => {
		return (
			<button
				onClick={() => {
					if (!team) return;
					// Todo make button focus team
				}}
				className={`flex items-center gap-2 px-4 py-3 text-left ${channelId === 'global' ? 'bg-theme-50 data-[active=true]:bg-theme-200' : 'hover:bg-gray-50 data-[active=true]:bg-theme-100'}`}
			>
				<TeamAvatar avatar={team?.data?.avatar} eventId={eventId!} teamId={team?.id ?? ''} />

				<div className="flex w-32 flex-col">
					<span className={`truncate font-bold`}>{label}</span>
				</div>
				<Spacer />
				<div className="flex flex-col items-end gap-1">
					{team?.data.timestamp && team.data.timestamp > 0 && (
						<span className="text-sm text-gray-500">{formatDate(team.data.timestamp)}</span>
					)}
				</div>
			</button>
		);
	};

	return (
		<AdminCard title="Map">
			<div
				data-screen={screen}
				className="group/map flex h-[400px] flex-row overflow-hidden rounded-lg border border-gray-200 bg-white"
			>
				<div className="hidden w-full flex-col overflow-auto group-data-[screen=channel-list]/map:flex lg:flex lg:w-1/3 lg:border-r">
					{teams
						.sort((a, b) => b.data.timestamp - a.data.timestamp)
						.map((team) => (
							<ChannelButton key={team.id} label={team.name} channelId={team.id} team={team} />
						))}
				</div>
				<div className="flex-1 flex-col gap-2 group-data-[screen=map]/map:flex lg:flex">
					<div className="h-96 w-full bg-black">
						<EventMap taskMarkers={markers} playerLocations={playerLocations} />
					</div>
				</div>
			</div>
		</AdminCard>
	);
};

export type EventOverview = MessageReturnTypes['get-event-overview'];

/**
 * A custom hook that loads all data required to create an event overview
 * and patches it up in realtime with updates received through socket events
 * @param id the id of the event to load
 * @returns the event data, or null when loading
 */
function useEventOverview(id: string) {
	const [data, setData] = useState<EventOverview | null>(null);

	const cleanupPromise = useRef<null | Promise<unknown>>(null);
	const isMounted = useRef(true);

	// TODO: maybe explore libraries that make this easier...
	useEffect(() => {
		isMounted.current = true;

		const handleChatMessage = (payload: { message: ChatMessageRecord }) => {
			if (!isMounted.current) return;

			console.log('handleChatMessage', payload);

			setData((prev) =>
				!prev
					? null
					: {
							...prev,
							chatMessages: [...(prev.chatMessages || []), payload.message],
						},
			);
		};

		const handleTeamLocationUpdate = (payload: { teamId: string; location: { lat: number; lng: number; heading: number | null } }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				if (!prev) return null;

				const teams = prev.teams?.map((team) =>
					team.id === payload.teamId
						? { ...team, data: { ...TeamDataSchema.parse(team.data ?? {}), ...payload.location } }
						: team,
				);

				return !teams ? null : { ...prev, teams };
			});
		};

		const handleTeamScoreUpdate = (payload: { teamId: string; score: number }) => {
			if (!isMounted.current) return;

			setData((prev) =>
				!prev
					? null
					: {
							...prev,
							teams: prev.teams?.map((team) => (team.id === payload.teamId ? { ...team, score: payload.score } : team)),
						},
			);
		};

		const handleTaskSubmission = (payload: { submission: TaskStateRecord }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				const taskSubmissions = prev?.taskSubmissions ?? [];
				const index = taskSubmissions.findIndex(
					(s) => s.taskId === payload.submission.taskId && s.teamId === payload.submission.teamId,
				);
				if (index === -1) {
					taskSubmissions.push(payload.submission);
				} else {
					taskSubmissions[index] = payload.submission;
				}
				taskSubmissions.sort((a, b) => a.updatedAt?.localeCompare(b?.updatedAt || 'zz') || 0);
				return !prev ? null : { ...prev, taskSubmissions };
			});
		};

		const handleEventStatusChanged = (payload: { status: EventStatus }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				return !prev ? null : { ...prev, event: !prev.event ? undefined : { ...prev.event, status: payload.status } };
			});
		};

		const handleEventArchiveChanged = (payload: { archived: boolean }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				return !prev ? null : { ...prev, event: !prev.event ? undefined : { ...prev.event, archived: payload.archived } };
			});
		};

		const handleEventFrozen = (payload: { frozen: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				return !prev
					? null
					: {
							...prev,
							event: !prev.event ? undefined : { ...prev.event, state: { ...prev.event.state, frozen: payload.frozen } },
						};
			});
		};

		const handleTeamJoined = (payload: { teamId: string; name: string; score: number; rejoinCode: string }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				return !prev
					? null
					: {
							...prev,
							teams: [
								...(prev.teams || []),
								{
									id: payload.teamId,
									name: payload.name,
									score: payload.score,
									data: TeamDataSchema.parse({}),
									captainId: 'dont need to know other team captain',
									rejoinCode: payload.rejoinCode,
								},
							],
						};
			});
		};

		const handleExistingTeamJoined = (payload: { deviceId: string; teamId: string; name: string }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				if (!prev) return null;
				const teamIndex = prev.teams!.findIndex((x) => x.id == payload.teamId);
				if (!prev.teams![teamIndex]!.data) return prev;
				if (prev.teams![teamIndex]!.data.devices.some((d) => d.id === payload.deviceId)) return prev;
				prev.teams![teamIndex]!.data.devices = [...(prev.teams?.[teamIndex]?.data?.devices || []), { id: payload.deviceId }];
				return {
					...prev,
					teams: prev.teams,
				};
			});
		};

		const handleRemoveDevice = (payload: { deviceId: string; teamId: string }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				if (!prev) return null;
				const teamIndex = prev.teams!.findIndex((x) => x.id == payload.teamId);
				if (!prev.teams![teamIndex]!.data.devices.some((d) => d.id === payload.deviceId)) return prev;
				prev.teams![teamIndex]!.data.devices = prev.teams![teamIndex]!.data.devices.filter((x) => x.id != payload.deviceId);
				return {
					...prev,
					teams: prev.teams,
				};
			});
		};

		const handleTeamDeleted = (payload: { teamId: string }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				return !prev
					? null
					: {
							...prev,
							teams: prev.teams?.filter((t) => t.id != payload.teamId),
						};
			});
		};

		const handleAvatarUpdate = (payload: { teamId: string; avatar: FileUpload | null }) => {
			if (!isMounted.current) return;

			setData((prev) => {
				return !prev
					? null
					: {
							...prev,
							teams: prev.teams?.map((team) =>
								team.id === payload.teamId
									? {
											...team,
											data: { ...TeamDataSchema.parse(team.data ?? {}), avatar: payload.avatar },
										}
									: team,
							),
						};
			});
		};

		const handleMessagesRead = (payload: { state: EventState }) => {
			if (!isMounted.current) return;

			setData((prev) =>
				!prev
					? null
					: {
							...prev,
							event: { ...prev.event!, state: { ...payload.state! } },
						},
			);
		};

		const handleRoundUnlockGlobal = (payload: { roundId: string; unlocked: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const roundStates = prev?.gameRoundStates ?? [];
				const hasChangedRound = roundStates.find((s) => s.roundId === payload.roundId);

				let updatedRoundStates;
				if (!hasChangedRound) {
					// append new round state
					updatedRoundStates = [...roundStates, { roundId: payload.roundId, teamId: null, unlocked: payload.unlocked }];
				} else {
					// update existing round state
					updatedRoundStates = roundStates.map((s) => (s.roundId === payload.roundId ? { ...s, unlocked: payload.unlocked } : s));
				}
				return !prev ? null : { ...prev, gameRoundStates: updatedRoundStates };
			});
		};

		const handleRoundUnlock = (payload: { roundIds: string[]; teamId: string; unlocked: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const roundStates = prev?.gameRoundStates ?? [];
				const updatedRoundStates = roundStates.map((s) => {
					const shouldChange = payload.roundIds.includes(s.roundId) && s.teamId === payload.teamId;
					return shouldChange ? { ...s, unlocked: payload.unlocked } : s;
				});
				return !prev ? null : { ...prev, gameRoundStates: updatedRoundStates };
			});
		};

		const handleChangeTaskUnlock = (payload: { taskIds: string[]; teamId: string; unlocked: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const taskSubmissions = prev?.taskSubmissions ?? [];
				const updatedTaskSubmissions = taskSubmissions.map((s) =>
					payload.taskIds.includes(s.taskId) && s.teamId === payload.teamId ? { ...s, unlocked: payload.unlocked } : s,
				);

				return !prev ? null : { ...prev, taskSubmissions: updatedTaskSubmissions };
			});
		};

		const handleChangeTaskUnlockGlobal = (payload: { taskId: string; unlocked: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const taskSubmissions = prev?.taskSubmissions ?? [];
				const updatedTaskSubmissions = taskSubmissions.map((s) =>
					s.taskId === payload.taskId ? { ...s, unlocked: payload.unlocked } : s,
				);
				return !prev ? null : { ...prev, taskSubmissions: updatedTaskSubmissions };
			});
		};

		const handleRegisterMembers = (payload: { teamId: string; members: { id: string; name: string }[]; ratId: string }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const updatedTeams = prev?.teams?.map((team) =>
					team.id === payload.teamId
						? {
								...team,
								data: {
									...TeamDataSchema.parse(team.data ?? {}),
									members: payload.members,
									ratId: payload.ratId,
								},
							}
						: team,
				);
				return !prev ? null : { ...prev, teams: updatedTeams };
			});
		};

		const handleChangeRat = (payload: { teamId: string; ratId: string }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const teams = prev?.teams?.map((team) =>
					team.id === payload.teamId
						? {
								...team,
								data: { ...TeamDataSchema.parse(team.data ?? {}), ratId: payload.ratId },
							}
						: team,
				);
				return !prev ? null : { ...prev, teams };
			});
		};
		const handleChangeCaptain = (payload: { teamId: string; captainId: string }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const teams = prev?.teams?.map((team) =>
					team.id === payload.teamId
						? {
								...team,
								captainId: payload.captainId,
							}
						: team,
				);
				return !prev ? null : { ...prev, teams };
			});
		};

		const handleHideScore = (payload: { hideScore: boolean }) => {
			if (!isMounted.current) return;
			setData((prev) => {
				const game = prev?.game?.game;
				if (!game) return prev;
				const newGame = { ...game, hideScore: payload.hideScore };
				return { ...prev, game: { ...prev.game!, game: newGame } };
			});
		};

		const handleConnect = async () => {
			const responseData = await emitMessage({ kind: 'get-event-overview', id });
			if (!isMounted.current) return;
			setData(responseData);
		};

		const initFn = async () => {
			try {
				console.log('run initFn');
				handleConnect();

				if (!isMounted.current) return;

				socket?.on('event-chat-message', handleChatMessage);
				socket?.on('team-score-update', handleTeamScoreUpdate);
				socket?.on('event-team-location-update', handleTeamLocationUpdate);
				socket?.on('event-task-submission', handleTaskSubmission);
				socket?.on('event-status-changed', handleEventStatusChanged);
				socket?.on('event-archive-changed', handleEventArchiveChanged);
				socket?.on('event-team-joined', handleTeamJoined);
				socket?.on('event-team-joined-existing', handleExistingTeamJoined);
				socket?.on('event-remove-device', handleRemoveDevice);
				socket?.on('event-team-deleted', handleTeamDeleted);
				socket?.on('team-avatar-update', handleAvatarUpdate);
				socket?.on('event-messages-read-update', handleMessagesRead);
				socket?.on('event-change-round-unlock-global-received', handleRoundUnlockGlobal);
				socket?.on('event-change-round-unlock-received', handleRoundUnlock);
				socket?.on('event-change-task-unlock-received', handleChangeTaskUnlock);
				socket?.on('event-change-task-unlock-global-received', handleChangeTaskUnlockGlobal);
				socket?.on('event-register-members', handleRegisterMembers);
				socket?.on('event-change-rat', handleChangeRat);
				socket?.on('event-change-captain', handleChangeCaptain);
				socket?.on('event-set-hide-score', handleHideScore);
				socket?.on('event-frozen', handleEventFrozen);
				socket?.on('connect', handleConnect);
			} catch (error) {
				console.error('Failed to initialize event overview:', error);
			}
		};

		// in case the previous event is being cleaned up
		// wait for this to finish before initializing the new event
		const initPromise = cleanupPromise.current
			? cleanupPromise.current.then(() => {
					console.log('init after cleanup');
					initFn();
				})
			: (() => {
					console.log('init right away');
					return initFn();
				})();

		return () => {
			isMounted.current = false;

			// in case the current event is still initializing
			// wait for this to happen before we clean it up
			// also keep a reference to this cleanup promise
			// so future initializations can wait for
			// this cleanup to finish first
			cleanupPromise.current = new Promise<void>((resolve) => {
				initPromise.then(() => {
					console.log('overview cleanup');
					socket?.off('event-chat-message', handleChatMessage);
					socket?.off('team-score-update', handleTeamScoreUpdate);
					socket?.off('event-task-submission', handleTaskSubmission);
					socket?.off('event-team-location-update', handleTeamLocationUpdate);
					socket?.off('event-status-changed', handleEventStatusChanged);
					socket?.off('event-archive-changed', handleEventArchiveChanged);
					socket?.off('event-team-joined', handleTeamJoined);
					socket?.off('event-team-joined-existing', handleExistingTeamJoined);
					socket?.off('event-remove-device', handleRemoveDevice);
					socket?.off('event-team-deleted', handleTeamDeleted);
					socket?.off('team-avatar-update', handleAvatarUpdate);
					socket?.off('event-messages-read-update', handleMessagesRead);
					socket?.off('event-change-round-unlock-global-received', handleRoundUnlockGlobal);
					socket?.off('event-change-round-unlock-received', handleRoundUnlock);
					socket?.off('event-change-task-unlock-received', handleChangeTaskUnlock);
					socket?.off('event-change-task-unlock-global-received', handleChangeTaskUnlockGlobal);
					socket?.off('event-register-members', handleRegisterMembers);
					socket?.off('event-change-rat', handleChangeRat);
					socket?.off('event-change-captain', handleChangeCaptain);
					socket?.off('event-set-hide-score', handleHideScore);
					socket?.off('event-frozen', handleEventFrozen);
					socket?.off('connect', handleConnect);

					emitMessage({ kind: 'event-leave-overview', id: id }).then(() => {
						console.log('cleanup done');
						cleanupPromise.current = null;
						resolve();
					});
				});
			});
		};
	}, [id]);

	return data;
}

const TaskAnswerPreview = ({ answer, teamId, eventId, task }: { answer: TaskAnswer; teamId: string; eventId: string; task: GameTask }) => {
	const { showModal, cancelModal } = useModal();
	return (
		<div className="h-full overflow-hidden">
			{answer.kind == 'text' && (
				<div className="h-full rounded-md bg-theme-200 p-3">
					<span className="font-semibold">Answer:</span>
					<span className="italic">"{answer.text}"</span>
				</div>
			)}
			{answer.kind == 'bingo' && (
				<div className="grid h-full grid-cols-5 gap-1 rounded-xl bg-gray-200 p-2">
					{answer.card.map((num, index) => {
						const isChecked = answer.checked.includes(index);

						return (
							<div
								key={index}
								className={`relative flex aspect-square items-center justify-center overflow-hidden rounded-md border`}
							>
								<img
									src={import.meta.env.VITE_GAME_URL + bingoSetToPath(task.typeBingoConfig?.set ?? 'numbers', num)}
									alt={`${num + 1}`}
									className="size-fit object-contain"
								/>

								{/* Stamp overlay */}
								<span className="pointer-events-none absolute flex items-center justify-center">
									<img
										src={import.meta.env.VITE_GAME_URL + '/bingo/bingo-stamp.png'}
										alt="stamped"
										style={{
											opacity: isChecked ? 0.9 : 0,
											transform: 'scale(1)',
										}}
										className="size-full object-contain"
									/>
								</span>
							</div>
						);
					})}
				</div>
			)}
			{answer.kind == 'video' && (
				<>
					<div className="relative flex flex-col items-center overflow-hidden rounded-md md:bg-theme-200 md:p-3">
						<div className="relative w-full overflow-hidden rounded-md">
							<BunnyVideoPlayer
								autoplay={false}
								videoId={answer.id}
								libraryId={import.meta.env.VITE_BUNNY_VIDEO_GAME_LIBRARY_ID}
								className="w-full rounded-md"
							/>
						</div>

						{answer.message && (
							<div className="mt-2 w-full rounded-md bg-white/60 p-3 text-sm text-gray-800 backdrop-blur-sm md:bg-white/40 md:text-gray-900">
								{answer.message}
							</div>
						)}
					</div>
				</>
			)}

			{answer.kind == 'photo' && (
				<>
					<div className="relative flex flex-col items-center overflow-hidden rounded-md md:bg-theme-200 md:p-3">
						<div className="relative flex h-48 items-center justify-center overflow-hidden rounded-md md:h-80 md:bg-theme-200 md:p-3">
							<img
								src={getPhotoUrl(answer.id, getFileExtension(answer.filename)!, teamId, eventId)}
								className="size-full rounded-md object-cover object-center"
								alt="Task Answer"
							/>
							<button
								className="absolute bottom-6 left-1/2 flex -translate-x-1/2 rounded-full border-2 border-gray-200 bg-white p-1"
								onClick={() => {
									showModal(
										<div className="max-size-screen relative">
											<img
												src={getPhotoUrl(answer.id, getFileExtension(answer.filename)!, teamId, eventId)}
												className="rounded-md"
												alt="Task Answer"
											/>
											,
											<button
												className="absolute bottom-8 left-1/2 flex -translate-x-1/2 rounded-full border-2 border-gray-200 bg-white p-1"
												onClick={() => cancelModal()}
											>
												<Minimize2 className="size-5" />
											</button>
										</div>,
									);
								}}
							>
								<Maximize2 className="size-5" />
							</button>
						</div>

						{answer.message && (
							<div className="mt-2 w-full rounded-md bg-white/60 p-3 text-sm text-gray-800 backdrop-blur-sm md:bg-white/40 md:text-gray-900">
								{answer.message}
							</div>
						)}
					</div>
				</>
			)}

			{(answer.kind == 'single-choice' || answer.kind == 'multiple-choice') && (
				<div className="h-full rounded-md bg-theme-200 p-3">
					<span className="font-semibold">Answer:</span>
					{(() => {
						const multipleChoiceAnswers = task.typeAutomaticValidationConfig?.multipleChoiceAnswers;
						const ids = answer.kind == 'single-choice' ? [answer.chosenId] : answer.chosenIds;
						if (multipleChoiceAnswers && multipleChoiceAnswers.length > 0)
							return ids.map((c) => {
								const answer = multipleChoiceAnswers.find((m) => m.id === c);
								return <div className="flex w-full items-center rounded-md bg-white p-4 text-black">{answer?.name}</div>;
							});
					})()}
				</div>
			)}
		</div>
	);
};

const TaskQuestionPreview = ({ node }: { node: MediaNode }) => {
	// NOTE: other types are auto validated, so they should never show up here.
	const { showModal, cancelModal } = useModal();

	return (
		<div className="h-full overflow-hidden">
			{node.kind == 'video' && (
				<div className="rounded-md md:bg-gray-200 md:p-3">
					<BunnyVideoPlayer autoplay={false} videoId={node.id} libraryId={import.meta.env.VITE_BUNNY_VIDEO_LIBRARY_ID} />
				</div>
			)}
			{node.kind == 'image' && (
				<div className="relative flex h-48 items-center justify-center overflow-hidden rounded-md bg-gray-200 md:h-80 md:p-3">
					<img
						src={getStorageUrl(node, import.meta.env.VITE_CDN)}
						className="max-h-full max-w-full rounded-md object-contain"
						alt="Task Answer"
					/>
					<button
						className="absolute bottom-6 left-1/2 flex -translate-x-1/2 rounded-full border-2 border-gray-200 bg-white p-1"
						onClick={() => {
							showModal(
								<div className="relative grid h-dvh w-dvw place-items-center p-4 md:p-6">
									<img
										src={getStorageUrl(node, import.meta.env.VITE_CDN)}
										className="block max-h-[calc(100dvh-5rem)] max-w-[calc(100dvw-5rem)] rounded-md object-contain"
										alt="Task Answer"
									/>
									,
									<button
										className="absolute bottom-8 left-1/2 flex -translate-x-1/2 rounded-full border-2 border-gray-200 bg-white p-1"
										onClick={() => cancelModal()}
									>
										<Minimize2 className="size-5" />
									</button>
								</div>,
							);
						}}
					>
						<Maximize2 className="size-5" />
					</button>
				</div>
			)}
		</div>
	);
};

export function getFileExtension(filename: string | undefined): string | undefined {
	return filename?.split('.').pop();
}

export function getPhotoUrl(id: string, ext: string, teamId: string, eventId: string, library: 'console' | 'client' = 'client') {
	return `https://${library == 'client' ? import.meta.env.VITE_CDN_GAME : import.meta.env.VITE_CDN}/${eventId}/${teamId}/${id}.${ext}`;
}

function getUnreadMessageCount(messages: ChatMessageRecord[], state: EventState) {
	const teamReadMessages = state?.teamChatMessagesRead
		? Object.entries(state?.teamChatMessagesRead).reduce((carry, [_key, messagesRead]) => {
				carry += messagesRead;
				return carry;
			}, 0) || 0
		: 0;
	const globalMessagesRead = state.globalChatMessagesRead || 0;
	return (messages?.length || 0) - (teamReadMessages + globalMessagesRead);
}
